import { FilterOption } from "@common/components/filterPopover/FilterPopover.types.ts";
import { FilterOperatorUi, ListFilter, ListFilterOperator, TextFilter, TextFilterOperator } from "@common/api/filterTypes.ts";
import { hasFilterValue } from "@common/components/filterBar/filterPinning.ts";
import { uiOperatorToWire } from "@common/components/filterBar/filterOperatorHydration.ts";
import { customFieldFilterIdPrefix } from "@common/components/filterPopover/customFieldFilterOption.ts";
import { LegalEntityFilterRequest, LegalEntitySortDirection } from "./LegalEntities.types.ts";
import { RiskLevel } from "../legalEntityClassificationTab/LegalEntityClassificationTab.types";
import { LegalEntityAuditStatus } from "../legalEntityAuditTab/LegalEntityAudit.types";
import { LegalEntityColumn } from "./legalEntityColumns.ts";

export const defaultLegalEntityPageSize = 50;

// Filter ids reuse the shared /filter-data vocabulary wherever one exists (business-area-ids, areas,
// responsible-ids, tag-ids), so those chips resolve their labels through the existing endpoint instead of
// needing a new entry in FilterDataHandler.
export const LEGAL_ENTITY_FILTER_ID_TO_BACKEND_PROPERTY: Record<string, string> = {
    "show-only-mine": "showOnlyMine",
    "legal-entity-status": "isActive",
    "country-codes": "countryCodes",
    "business-area-ids": "businessAreaIds",
    "classification-questionnaire-ids": "classificationQuestionnaireIds",
    "classification-risk-levels": "classificationRiskLevels",
    areas: "complianceAreaIds",
    "risk-area-levels": "riskAreaLevels",
    "responsible-ids": "responsibleIds",
    "latest-audit-questionnaire-ids": "latestAuditQuestionnaireIds",
    "latest-audit-statuses": "latestAuditStatuses",
    "legal-entity-types": "types",
    "preselected-on-create": "isPreselectedOnCreate",
    "tag-ids": "tagIds",
    // legal-entity-ids and document-type-ids are ids FilterDataHandler already resolves chip labels for.
    "legal-entity-ids": "legalEntityIds",
    locations: "locations",
    "tia-not-necessary": "isTransferImpactAssessmentUnnecessary",
    "sender-on-source": "isSenderOnSource",
    "sender-on-data-processor": "isSenderOnDataProcessor",
    "data-processor": "isDataProcessor",
    "sub-processor": "isSubProcessor",
    "has-documents": "hasDocuments",
    "document-type-ids": "documentTypeIds",
    "has-sharings": "hasSharings",
    roles: "roles",
    "has-processing-activities": "hasProcessingActivities",
    "sub-contractor-ids": "subContractorIds",
    "transfer-basis-not-necessary": "isTransferBasisUnnecessary",
    "recipient-on-disclosure": "isRecipientOnDisclosure",
    approved: "isApproved",
    "supervision-types": "supervisionTypes",
    "supervision-frequencies": "supervisionFrequencies",
    // "certifications", "transfer-basis-ids", "data-categories" and "data-subjects" are the ids the system
    // filter uses too, so FilterDataHandler already resolves their chips.
    certifications: "certificationIds",
    "transfer-basis-ids": "transferBasisIds",
    "data-categories": "dataCategoryIds",
    "data-subjects": "dataSubjectIds",
    "document-owner-ids": "documentOwnerIds",
    "document-group-entity-ids": "documentGroupEntityIds",
    "has-data-categories": "hasDataCategories",
    "has-data-subjects": "hasDataSubjects",
    note: "note",
    website: "website",
    description: "description",
    address: "address",
    "zip-code": "zipCode",
    city: "city",
    "company-number": "companyNumber",
    "reference-number": "referenceNumber",
    contacts: "contacts",
};

const ID_LIST_PROPERTIES = [
    "countryCodes",
    "types",
    "businessAreaIds",
    "complianceAreaIds",
    "responsibleIds",
    "tagIds",
    "classificationQuestionnaireIds",
    "latestAuditQuestionnaireIds",
    "riskAreaLevels",
    "legalEntityIds",
    "documentTypeIds",
    "subContractorIds",
    "certificationIds",
    "transferBasisIds",
    "documentOwnerIds",
    "documentGroupEntityIds",
    "dataCategoryIds",
    "dataSubjectIds",
];

const NUMERIC_LIST_PROPERTIES = ["classificationRiskLevels", "latestAuditStatuses", "locations", "roles", "supervisionTypes", "supervisionFrequencies"];

const BOOLEAN_PROPERTIES = [
    "showOnlyMine",
    "isActive",
    "isPreselectedOnCreate",
    "isTransferImpactAssessmentUnnecessary",
    "isSenderOnSource",
    "isSenderOnDataProcessor",
    "isDataProcessor",
    "isSubProcessor",
    "hasDocuments",
    "hasSharings",
    "hasProcessingActivities",
    "isTransferBasisUnnecessary",
    "isRecipientOnDisclosure",
    "isApproved",
    "hasDataCategories",
    "hasDataSubjects",
];

const TEXT_PROPERTIES = ["description", "address", "zipCode", "city", "companyNumber", "referenceNumber", "contacts", "note", "website"];

// A text option's value is the typed string until the operator menu wraps it; hydrateFilterOperators unwraps a
// stored TextFilter back to the string, so both shapes arrive here.
function toTextFilter(option: FilterOption): TextFilter | undefined {
    if (typeof option.value === "string") {
        return {
            value: option.value,
            filterOperator: option.filterOperator === FilterOperatorUi.Negative ? TextFilterOperator.NotContains : TextFilterOperator.Contains,
        };
    }

    if (typeof option.value === "object" && "value" in option.value && "filterOperator" in option.value) {
        return option.value as TextFilter;
    }

    return undefined;
}

function toListFilter(option: FilterOption, values: Array<string | number>) {
    return {
        value: values,
        filterOperator: option.filterOperator === FilterOperatorUi.Negative ? ListFilterOperator.NotIn : ListFilterOperator.In,
    };
}

// The single-select booleans keep their value in an array so chips and persistence behave like every other
// filter, and the Boolean options hold a plain bool; either way the request carries a bool the backend can treat
// as "unset when absent".
function extractBoolean(value: any): boolean | undefined {
    const selected = Array.isArray(value) ? value[0] : value;
    if (selected === undefined || selected === "") {
        return undefined;
    }

    return selected === true || selected === "true";
}

export function transformLegalEntityFilterOptionsToBackend(
    filterOptions: FilterOption[],
    sortByColumn?: LegalEntityColumn,
    sortDirection?: LegalEntitySortDirection
): Partial<LegalEntityFilterRequest> {
    const result: Partial<LegalEntityFilterRequest> = {};

    filterOptions.forEach((option) => {
        if (!hasFilterValue(option.value)) {
            return;
        }

        // A custom field has no entry in the map above: its backend property is the field id itself, and its
        // value is passed through as the submenu produced it, for the backend to read by input type.
        if (option.id.startsWith(customFieldFilterIdPrefix)) {
            result.customFieldFilters ??= [];
            result.customFieldFilters.push({
                inputFieldId: option.id.replace(customFieldFilterIdPrefix, ""),
                value: option.value,
                operator: uiOperatorToWire(option.filterOperator, option.type),
            });
            return;
        }

        const backendProperty = LEGAL_ENTITY_FILTER_ID_TO_BACKEND_PROPERTY[option.id];
        if (!backendProperty) {
            return;
        }

        if (BOOLEAN_PROPERTIES.includes(backendProperty)) {
            (result as any)[backendProperty] = extractBoolean(option.value);
            return;
        }

        if (TEXT_PROPERTIES.includes(backendProperty)) {
            (result as any)[backendProperty] = toTextFilter(option);
            return;
        }

        if (ID_LIST_PROPERTIES.includes(backendProperty)) {
            if (Array.isArray(option.value)) {
                (result as any)[backendProperty] = toListFilter(option, option.value);
            } else if (typeof option.value === "object" && "value" in option.value && "filterOperator" in option.value) {
                (result as any)[backendProperty] = option.value as ListFilter<string>;
            }
            return;
        }

        if (NUMERIC_LIST_PROPERTIES.includes(backendProperty)) {
            if (Array.isArray(option.value)) {
                (result as any)[backendProperty] = toListFilter(option, option.value.map(Number));
            } else if (typeof option.value === "object" && "value" in option.value && "filterOperator" in option.value) {
                (result as any)[backendProperty] = option.value as ListFilter<RiskLevel | LegalEntityAuditStatus>;
            }
        }
    });

    result.pageNumber = 1;
    result.pageSize = defaultLegalEntityPageSize;
    result.sortByColumn = sortByColumn;
    result.sortDirection = sortDirection;

    return result;
}
